import generateRangePicker from 'antd/es/date-picker/generatePicker/generateRangePicker'
import type { Moment } from 'moment/moment'
import momentGenerateConfig from 'rc-picker/lib/generate/moment'
import React, { PropsWithChildren, useEffect, useState } from 'react'
import { useOutsideClick } from '../../../lib/hooks/UseOutsideClick'
import { RangePickerDateProps } from 'antd/es/date-picker/generatePicker'
import { TriangleButton } from '../../buttons/triangle/TriangleButton'
import { DateIcon } from '../../icons/filters/DateIcon'

import styles from './DateSelect.module.css'
import { IconArrowRight } from '../../icons/IconArrowRight'
import { IconCheck } from '../../icons/IconCheck'
import moment from 'moment'
import { DropdownPositioner } from '../../../../lib/ui/dropdown-positioner/component/DropdownPositioner'

export type IDateSelectProps = RangePickerDateProps<Moment>

const RangePicker = generateRangePicker<Moment>(momentGenerateConfig)
const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD'

export const DateSelect = ({ onChange, ...props }: IDateSelectProps) => {
  const [pickerValue, setPickerValue] = useState(props.defaultValue)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [calendarSelection, setCalendarSelection] = useState(0)

  const clickRef = useOutsideClick(() => {
    if (calendarOpen && pickerValue) {
      onChange?.(pickerValue, [
        moment(pickerValue[0]).format(DEFAULT_DATE_FORMAT),
        moment(pickerValue[1]).format(DEFAULT_DATE_FORMAT),
      ])

      setCalendarOpen(false)
    }

    if (dropdownOpen) {
      setDropdownOpen(false)
    }
  })

  const presetRanges = [
    {
      label: 'Today',
      start: moment().format(DEFAULT_DATE_FORMAT),
      end: moment().format(DEFAULT_DATE_FORMAT),
    },
    {
      label: 'Yesterday',
      start: moment().subtract(1, 'day').format(DEFAULT_DATE_FORMAT),
      end: moment().subtract(1, 'day').format(DEFAULT_DATE_FORMAT),
    },
    {
      label: 'Week',
      start: moment().subtract(1, 'weeks').format(DEFAULT_DATE_FORMAT),
      end: moment().format(DEFAULT_DATE_FORMAT),
    },
    {
      label: 'Month',
      start: moment().subtract(1, 'months').format(DEFAULT_DATE_FORMAT),
      end: moment().format(DEFAULT_DATE_FORMAT),
    },
    {
      label: '3 Month',
      start: moment().subtract(3, 'months').format(DEFAULT_DATE_FORMAT),
      end: moment().format(DEFAULT_DATE_FORMAT),
    },
  ]

  useEffect(() => {
    if (calendarOpen) {
      if (calendarSelection === 2 && pickerValue) {
        setCalendarOpen(false)

        onChange?.(pickerValue, [
          moment(pickerValue[0]).format(DEFAULT_DATE_FORMAT),
          moment(pickerValue[1]).format(DEFAULT_DATE_FORMAT),
        ])
      }
    } else {
      setCalendarSelection(0)
    }
  }, [calendarOpen, calendarSelection])

  useEffect(() => {
    setPickerValue(props.value)
  }, [props.value])

  // выбранный пресет — тот, с которым совпадает текущий диапазон
  const isPresetSelected = ({ start, end }: { start: string; end: string }) =>
    !!pickerValue?.[0] &&
    !!pickerValue?.[1] &&
    moment(pickerValue[0]).format(DEFAULT_DATE_FORMAT) === start &&
    moment(pickerValue[1]).format(DEFAULT_DATE_FORMAT) === end

  // как опция селекта (ResultItem)
  const Option = ({
    children,
    selected = false,
    onClick,
  }: PropsWithChildren<{ selected?: boolean; onClick: () => void }>) => {
    return (
      <button
        className={`flex w-full cursor-pointer items-center rounded-[8px] p-[8px] text-start text-[15px] leading-[24px] hover:bg-default-200 ${selected ? 'text-aqua-300' : 'text-focus-500 hover:text-default'}`}
        type='button'
        onClick={onClick}
      >
        {children}
        {selected && (
          <div className='ml-auto flex w-[20px] shrink-0 items-center pl-[5px]'>
            <IconCheck color='#00326D' />
          </div>
        )}
      </button>
    )
  }

  return (
    <div ref={clickRef} className={'cursor-pointer rounded-[8px] bg-grey-500 p-[4px]'}>
      {/* фокус-стейт как у Field, но только пока открыт список или календарь: после выбора дат
          фокус остаётся в поле ввода antd, и focus-within держал бы рамку у закрытого поля */}
      <div
        className={`relative flex h-[48px] items-center justify-between rounded-[6px] bg-white shadow-outline shadow-focus/[0.65] after:pointer-events-none after:absolute after:inset-0 after:rounded-[6px] after:ring-inset after:ring-default ${(dropdownOpen || calendarOpen) && 'after:ring-1'}`}
      >
        <div className={'pr-[10px] pl-[14px] text-focus-500'}>
          <DateIcon />
        </div>
        <RangePicker
          className={styles['range-picker']}
          {...props}
          inputReadOnly
          allowClear={false}
          defaultValue={pickerValue}
          disabledDate={(current) => current && current > moment().endOf('day')}
          open={calendarOpen}
          separator={<IconArrowRight className={'w-[12px]'} color={'#6684a7'} />}
          suffixIcon={null}
          value={pickerValue}
          onCalendarChange={(e) => {
            setPickerValue(e)
            setCalendarSelection((prevState) => prevState + 1)
          }}
          onClick={() => {
            if (calendarOpen) {
              setCalendarOpen(false)
              setDropdownOpen(true)
            } else {
              setDropdownOpen(true)
            }
          }}
        ></RangePicker>
        <div className={'px-[16px]'}>
          <TriangleButton
            color={'#A059FF'}
            position={dropdownOpen ? 'top' : 'down'}
          ></TriangleButton>
        </div>
        <DropdownPositioner open={dropdownOpen} position='auto'>
          <div className='flex flex-col overflow-hidden rounded-[6px] shadow-outline shadow-default'>
            <div className='flex flex-col overflow-auto bg-white p-[8px]'>
              {presetRanges.map((range, index) => (
                <Option
                  key={index}
                  selected={isPresetSelected(range)}
                  onClick={() => {
                    onChange?.([moment(range.start), moment(range.end)], [range.start, range.end])
                    setDropdownOpen(false)
                  }}
                >
                  {range.label}
                </Option>
              ))}
              <Option
                onClick={() => {
                  setDropdownOpen(false)
                  setCalendarOpen(true)
                }}
              >
                <div className={'flex w-full items-center justify-between'}>
                  Custom <IconArrowRight className={'w-[14px]'} color={'#A059FF'} />
                </div>
              </Option>
            </div>
          </div>
        </DropdownPositioner>
      </div>
    </div>
  )
}
